import {inject, Injectable} from '@angular/core';
import {BUTTON_COLOR} from '@discocil/fv-components';
import {Observable, timer} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {PaymentFeedbackPresenter} from './payment-feedback.presenter';
import {PaymentInteractionHandler} from './payment-interaction.handler';
import {PaymentCheckoutRecovery} from '../../domain/ports/ui/recovery.port';

enum PaymentRetryChoice {
    INTEGRATED,
    MANUAL,
    CANCEL,
}

@Injectable({providedIn: 'root'})
export class CheckoutRecoveryHandler implements PaymentCheckoutRecovery {
    private readonly interaction = inject(PaymentInteractionHandler);
    private readonly feedback = inject(PaymentFeedbackPresenter);

    recoverCheckout(): Observable<boolean> {
        this.feedback.hideLoading();

        return timer(300).pipe(
            switchMap(() => this.askForRetry({ allowManual: false })),
            tap(choice => {
                if (choice === PaymentRetryChoice.INTEGRATED) {
                    this.feedback.showLoading();
                }
            }),
            map(choice => choice === PaymentRetryChoice.INTEGRATED)
        );
    }

    private askForRetry(options: { allowManual: boolean }): Observable<PaymentRetryChoice> {
        return this.interaction.askUserChoice<PaymentRetryChoice>({
            titleKey: 'fv_confirm.payment.error_card_payment',
            descriptionKey: 'fv_confirm.payment.retry_payment_option_title',
            defaultValue: PaymentRetryChoice.CANCEL,
            options: [
                {
                    labelKey: 'global.retry',
                    value: PaymentRetryChoice.INTEGRATED,
                    style: { color: BUTTON_COLOR.error }
                },
                ...(options.allowManual
                    ? [{
                        labelKey: 'fv_confirm.payment.use_manual_tpv',
                        value: PaymentRetryChoice.MANUAL,
                        style: { color: BUTTON_COLOR.secondary }
                    }]
                    : []),
                {
                    labelKey: 'global.cancel',
                    value: PaymentRetryChoice.CANCEL,
                    style: { color: BUTTON_COLOR.transparent }
                },
            ],
        });
    }
}
